import * as api from './api'
import * as types from './types'
import { computed, ref, reactive } from 'vue'

const processTypeOptions = [
    {
        label: 'Sales',
        value: 'sales'
    },
    {
        label: 'Marketing',
        value: 'marketing'
    }
]
const subscriptionTypeOptions = [
    {
        label: 'Trial',
        value: 'trial'
    },
    {
        label: 'Registration',
        value: 'registration'
    }
]

const accessDurationTypeOptions = [
    {
        label: 'Access duration in days',
        value: 'accessAfterDays'
    },
    {
        label: 'Access end date',
        value: 'endDate'
    }
]

const freeForms = ref<types.FreeForm[]>([])
const freeFormsCount = ref<number>()
const freeForm = ref<types.FreeForm>()

const defaultSortBy = 'id desc'

export const freeFormsFilter = reactive({
    search: '',
    sortBy: null,
    limit: 10,
    offset: 0
})

const getFreeForms = () => {
    return api.getFreeForms(
        freeFormsFilter.search,
        freeFormsFilter.sortBy || defaultSortBy,
        freeFormsFilter.limit,
        freeFormsFilter.offset
    ).then(
        freeFormsList => {
            freeForms.value = freeFormsList.results
            freeFormsCount.value = freeFormsList.count
        }
    )
}

const addFreeForm = (payload: api.AddFreeFormRequest) => api.addFreeForm(payload).then(getFreeForms)

const withFreeFormUpdate = async (formId: string, callback: () => void) => {
    await callback()
    await getFreeForm(formId)
}

const getFreeForm = async (id: string) => {
    freeForm.value = await api.getFreeForm(id)
    if (freeForms.value.length) {
        const freeFormIndex = freeForms.value.findIndex(value => value.id === id)
        freeForms.value[freeFormIndex] = freeForm.value
    }
    return freeForm.value
}

const translationDomains = ref<types.TranslationDomain[]>([])
const translationDomainTranslations = ref<types.TranslationDomainTranslations[]>([])

const getTranslationDomains = () => api.availableTranslationDomains().then(translationDomainsList => {
    translationDomains.value = translationDomainsList
})

const getTranslationDomainTranslations = (domain: string) =>
    api.getTranslationDomainTranslations(domain).then(domainTranslations => {
        translationDomainTranslations.value = domainTranslations
    })

const getTranslationDomainByDomain = (domain: string) => {
    const domainIndex = translationDomains.value.findIndex(value => value.domain === domain)
    return translationDomains.value[domainIndex]
}

const addTranslationDomain = (payload: api.AddTranslationDomainRequest) =>
    api.addTranslationDomain(payload).then(getTranslationDomains)

const updateFreeFormName = (id: string, payload: api.UpdateFreeFormNameRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormName(id, payload))

const updateFreeFormSlug = (id: string, payload: api.UpdateFreeFormSlugRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormSlug(id, payload))

const updateFreeFormDescription = (id: string, payload: api.UpdateFreeFormDescriptionTranslationRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormDescription(id, payload))

const updateFreeFormAllowAfter = (id: string, payload: api.UpdateFreeFormAllowAfterRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormAllowAfter(id, payload))

const updateFreeFormPackages = (id: string, payload: api.UpdateFreeFormPackagesRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormPackages(id, payload))

const updateFreeFormEndDate = (id: string, payload: api.UpdateFreeFormEndDateRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormEndDate(id, payload))

const updateFreeFormAccessDuration = (id: string, payload: api.UpdateFreeFormAccessDurationRequest) =>
    withFreeFormUpdate(id, () => api.updateFreeFormAccessDuration(id, payload))

const enableFreeForm = (id: string) =>
    withFreeFormUpdate(id, () => api.enableFreeForm(id))

const disableFreeForm = (id: string) =>
    withFreeFormUpdate(id, () => api.disableFreeForm(id))

export function useFreeFormsModule () {
    return {
        getFreeForms,
        freeForms: computed(() => freeForms.value),
        freeFormsCount: computed(() => freeFormsCount.value),
        freeFormsFilter,
        getFreeForm,
        freeForm: computed(() => freeForm.value),
        addFreeForm,
        updateFreeFormName,
        updateFreeFormSlug,
        updateFreeFormDescription,
        updateFreeFormAllowAfter,
        updateFreeFormPackages,
        updateFreeFormEndDate,
        updateFreeFormAccessDuration,
        processTypeOptions,
        subscriptionTypeOptions,
        accessDurationTypeOptions,
        enableFreeForm,
        disableFreeForm,
        getTranslationDomains,
        getTranslationDomainTranslations,
        translationDomains: computed(() => translationDomains.value),
        translationDomainTranslations: computed(() => translationDomainTranslations.value),
        addTranslationDomain,
        getTranslationDomainByDomain
    }
}
